/**
 * Signaler une question du quiz de prérequis, en machine d'états pure : « Un souci avec cette
 * question ? », l'ancien `QuizQuestionSignalButton`.
 *
 * Le signalement s'ouvre sur la question en cours ; l'élève choisit le problème (un des quatre de
 * l'ancien code), écrit un commentaire s'il veut, et l'envoie. Envoyé, il se referme et l'écran dit
 * le mot de l'ancien, au tutoiement : « Merci pour ta réponse. Pour ne pas rester bloqué, choisis
 * tout de même une réponse. » Refusé, il reste ouvert, le choix et le commentaire gardés : l'élève
 * réessaie.
 */
export const QUIZ_SIGNAL_TYPES = ["incorrect_question", "incorrect_answer", "display_issue", "too_difficult"] as const

export type QuizSignalType = (typeof QUIZ_SIGNAL_TYPES)[number]

/**
 * L'accusé de l'ancien code, au tutoiement comme tous les refus de l'espace élève (Wajih, 23/10). L'ancien
 * espace garde le sien (`QuizQuestionSignalButton.js`), qui vouvoie : ils ne partagent pas la phrase.
 */
export const QUIZ_SIGNAL_SENT = "Merci pour ta réponse. Pour ne pas rester bloqué, choisis tout de même une réponse."

export type QuizSignalState = {
    open: boolean
    type: QuizSignalType | null
    comment: string
    sending: boolean
    /** L'accusé à dire, une fois. */
    notice: string | null
    /** L'envoi a échoué : le signalement reste ouvert. */
    failed: boolean
}

export const INITIAL_QUIZ_SIGNAL: QuizSignalState = {
    open: false,
    type: null,
    comment: "",
    sending: false,
    notice: null,
    failed: false,
}

export type QuizSignalEvent =
    | { type: "opened" }
    | { type: "closed" }
    | { type: "typeChosen"; signal: QuizSignalType }
    | { type: "commentChanged"; comment: string }
    | { type: "sendStarted" }
    | { type: "sent" }
    | { type: "sendFailed" }
    | { type: "noticeDismissed" }

export function quizSignalReducer(state: QuizSignalState, event: QuizSignalEvent): QuizSignalState {
    switch (event.type) {
        case "opened":
            return { ...INITIAL_QUIZ_SIGNAL, open: true, notice: state.notice }
        case "closed":
            return state.sending ? state : { ...INITIAL_QUIZ_SIGNAL, notice: state.notice }
        case "typeChosen":
            return state.sending ? state : { ...state, type: event.signal, failed: false }
        case "commentChanged":
            return state.sending ? state : { ...state, comment: event.comment }
        case "sendStarted":
            if (state.sending || state.type == null) return state
            return { ...state, sending: true, failed: false }
        case "sent":
            return { ...INITIAL_QUIZ_SIGNAL, notice: QUIZ_SIGNAL_SENT }
        case "sendFailed":
            return { ...state, sending: false, failed: true }
        case "noticeDismissed":
            return { ...state, notice: null }
        default:
            return state
    }
}
